import {useIntl} from 'react-intl';
import classNames from 'classnames';

import {TypographySize, useTypography} from '@time-webkit/all/hooks/typography';

import {MessageId, getMessageDescription, getMessageHeader} from './messages';
import {testPasswordComplexity} from './validation';
import type {PasswordValidationRules} from './types';

import styles from './styles.module.css';

interface Props {
    validationsRules: PasswordValidationRules;
    password: string;
    repeatPassword: string;
    passwordMessageId?: MessageId;
    repeatPasswordMessageId?: MessageId;
    passwordFocused: boolean;
    repeatPasswordFocused: boolean;
}

export const NewPasswordsError: React.FC<Props> = ({
    validationsRules,
    password = '',
    repeatPassword = '',
    passwordMessageId,
    repeatPasswordMessageId,
    passwordFocused,
    repeatPasswordFocused,
}) => {
    const intl = useIntl();

    const [header, description] = useTypography([
        {
            size: TypographySize.BodyMTrue,
        },
        {
            size: TypographySize.BodyM,
        },
    ]);

    const getPasswordMessageId = () => {
        if (passwordMessageId === MessageId.General) {
            return MessageId.General;
        }

        if (!passwordMessageId && password.length < validationsRules.PasswordMinimumLength && !passwordFocused) {
            return MessageId.Helper;
        }

        if (passwordFocused && password.length < validationsRules.PasswordMinimumLength) {
            return MessageId.Helper;
        }

        if (passwordFocused && password.length > validationsRules.PasswordMaximumLength) {
            return MessageId.TooLong;
        }

        if (passwordFocused && password.length >= validationsRules.PasswordMinimumLength) {
            return testPasswordComplexity(password, validationsRules) ? MessageId.Good : MessageId.TooWeak;
        }

        if (!passwordFocused && password && !passwordMessageId && password === repeatPassword) {
            return MessageId.Match;
        }

        if (!passwordFocused && password && repeatPassword && !passwordMessageId && repeatPasswordMessageId && !repeatPasswordFocused) {
            return repeatPasswordMessageId;
        }

        if (!passwordFocused && !passwordMessageId && repeatPassword.length >= password.length && password !== repeatPassword) {
            return MessageId.NoMatch;
        }

        if (!passwordFocused && !passwordMessageId && repeatPassword.length < password.length) {
            return MessageId.Good;
        }

        return passwordMessageId ?? MessageId.Helper;
    };

    const getLineWidth = (messageId: MessageId) => {
        if ((messageId === MessageId.NoMatch && repeatPassword.length <= password.length) || (messageId === MessageId.Good && repeatPasswordFocused)) {
            return ((100 / password.length) * repeatPassword.length) + '%';
        }
        if (messageId === MessageId.Helper || messageId === MessageId.TooShort || (messageId === MessageId.Good && repeatPasswordFocused && repeatPassword.length < validationsRules.PasswordMinimumLength)) {
            return ((100 / validationsRules.PasswordMinimumLength) * password.length) + '%';
        }

        if ((messageId === MessageId.Good && !repeatPassword) || [MessageId.TooWeak, MessageId.Match, MessageId.NoMatch, MessageId.General, MessageId.TooLong].includes(messageId)) {
            return '100%';
        }

        return '50%';
    };

    const getLineColorClass = (messageId: MessageId) => {
        if (messageId === MessageId.Helper || (messageId === MessageId.Good && repeatPasswordFocused && repeatPassword.length < password.length)) {
            return styles.info;
        }

        if (passwordFocused && messageId === MessageId.TooWeak) {
            return styles.warning;
        }

        if ((messageId === MessageId.Good && !repeatPasswordFocused) || messageId === MessageId.Match) {
            return styles.success;
        }

        return styles.error;
    };

    const id = getPasswordMessageId();

    return (
        <div className={styles.container}>
            <div className={styles.line_background}>
                <div
                    style={{width: getLineWidth(id)}}
                    className={classNames(styles.colored_line, getLineColorClass(id))}
                />
            </div>
            <div className={styles.text_container}>
                <div className={header}>{getMessageHeader(id, intl)}</div>
                <div className={description}>
                    {getMessageDescription(id, password, repeatPassword, validationsRules, intl)}
                </div>
            </div>
        </div>
    );
};
